/**
 * POSKI – Produktkarten: Bild-Reset
 *
 * Problem: Horizon tauscht das "zweite Bild bei Hover" nicht per CSS, sondern
 * scrollt eine echte Mini-Slideshow in der Karte weiter. Die Steuerung ist rein
 * relativ – `next()` beim Betreten, `previous()` beim Verlassen (product-card.js).
 * Geht eines der beiden Pointer-Events verloren, bleibt die Karte dauerhaft auf
 * Bild 2 stehen. Im Theme-Editor passiert das ständig, weil dort bei jeder
 * Einstellungsänderung der Section-DOM ausgetauscht wird, während der Mauszeiger
 * über der Vorschau liegt.
 *
 * Lösung: Wir setzen die Karte immer *absolut* auf Bild 1 zurück, statt relativ
 * einen Schritt zurückzugehen. Horizons eigene Logik bleibt unangetastet – dieses
 * Skript korrigiert nur hinterher.
 */

/** Zeigt die Galerie gerade etwas anderes als das erste Bild? */
function isDrifted(scroller) {
  return Math.abs(scroller.scrollLeft) > 1 || scroller.scrollTop > 1;
}

/**
 * Setzt eine Produktkarten-Galerie zurück auf das erste Bild.
 * @param {Element} gallery - Das `.card-gallery`-Element.
 * @param {object} [options]
 * @param {boolean} [options.force] - Auch zurücksetzen, wenn die Karte noch `:hover` meldet.
 *   Nötig, wenn die Maus das Fenster verlässt: der Hover-Zustand bleibt dann hängen.
 */
function resetGallery(gallery, { force = false } = {}) {
  // Wichtigste Sperre: Liegt die Maus auf der Karte, ist der Bildwechsel gewollt.
  // Ohne diese Prüfung kämpft das Skript gegen Horizons Hover-Logik und das Bild
  // flackert zwischen Bild 1 und 2 hin und her.
  if (!force && gallery.matches(':hover')) return;

  const slideshow = gallery.querySelector('slideshow-component');
  if (!slideshow) return;

  // Karten mit Farb-/Varianten-Auswahl regelt Horizon selbst korrekt: dort soll
  // das Bild der gewählten Variante stehen bleiben, nicht Bild 1. Nicht reinfunken.
  const card = gallery.closest('product-card');
  if (card?.querySelector('swatches-variant-picker-component')) return;

  const scroller = slideshow.querySelector('[ref="scroller"]');
  if (!scroller) return;

  // Erst über Horizons eigene API, damit der interne Index und die
  // aria-hidden-Zustände der Slides sauber mitgezogen werden.
  try {
    slideshow.select?.(0, undefined, { animate: false });
  } catch {
    // Interne API hat sich geändert – der harte Reset unten greift trotzdem.
  }

  // Danach die Scroll-Position hart nachziehen. Nötig, weil Horizons `select()`
  // sofort aussteigt, wenn der interne Index bereits 0 ist – die Slideshow aber
  // trotzdem verschoben dastehen kann (Desync nach Editor-Reload oder nachdem
  // der Browser beim Zurück-Navigieren die Scroll-Position wiederhergestellt hat).
  if (isDrifted(scroller)) {
    scroller.scrollTo({ left: 0, top: 0, behavior: 'instant' });
  }
}

/**
 * Setzt alle Galerien innerhalb eines Bereichs zurück.
 * @param {ParentNode} [root] - Wurzel, standardmäßig das ganze Dokument.
 */
function resetAll(root = document) {
  for (const gallery of root.querySelectorAll('.card-gallery')) {
    resetGallery(gallery);
  }
}

/** Nach Horizons synchronem Handler laufen, sonst überschreibt er uns wieder. */
function resetGalleryDeferred(gallery) {
  setTimeout(() => resetGallery(gallery), 0);
}

/** Wie oben, ignoriert aber einen hängengebliebenen :hover-Zustand. */
function resetGalleryForced(gallery) {
  setTimeout(() => resetGallery(gallery, { force: true }), 0);
}

/** Geräte mit echtem Mauszeiger. Auf Touch steigt Horizons Bildwechsel ohnehin aus
 *  (`pointerType !== 'mouse'`), und ein Wisch durch die Kartenbilder darf nicht
 *  abgewürgt werden. */
const hasMouse = window.matchMedia('(hover: hover)').matches;

/**
 * Sicherheitsnetz: Eine Karte, die ein anderes Bild zeigt, obwohl die Maus nicht auf
 * ihr liegt, ist immer falsch – egal wodurch sie dahin gekommen ist. Dieser Durchlauf
 * fängt daher auch Ursachen ab, die die Trigger unten gar nicht kennen.
 *
 * Bewusst nicht über `scroll`-Events oder IntersectionObserver gelöst: beides wird in
 * Hintergrund-Tabs und in gedrosselten iframes (wie der Editor-Vorschau) nicht
 * zuverlässig ausgeliefert. Stattdessen laufen wir gedrosselt bei Mausbewegung – also
 * genau dann, wenn jemand tatsächlich hinschaut.
 */
const SCAN_INTERVAL = 300;
let lastScan = 0;
let scanScheduled = false;

function scanForDrift() {
  scanScheduled = false;
  lastScan = Date.now();

  for (const gallery of document.querySelectorAll('.card-gallery')) {
    // Maus liegt auf der Karte: gewollter Hover-Wechsel, in Ruhe lassen.
    if (gallery.matches(':hover')) continue;

    const scroller = gallery.querySelector('slideshow-component [ref="scroller"]');
    if (scroller && isDrifted(scroller)) resetGallery(gallery);
  }
}

function requestScan() {
  if (!hasMouse || scanScheduled) return;
  scanScheduled = true;

  const wait = Math.max(0, SCAN_INTERVAL - (Date.now() - lastScan));
  setTimeout(scanForDrift, wait);
}

/**
 * Merkt sich die zuletzt überfahrene Karte. Wechselt der Zeiger auf eine andere
 * Karte oder ins Nichts, wird die vorherige zurückgesetzt – auch dann, wenn ihr
 * `pointerleave` nie angekommen ist. Das ist der Kern der Absicherung und kostet
 * nur einen `closest()`-Aufruf pro Zeigerwechsel.
 */
let lastHovered = null;

document.addEventListener(
  'pointerover',
  (event) => {
    // Muss vor dem Abbruch unten stehen: bewegt sich der Zeiger nur innerhalb
    // desselben Elements, käme das Sicherheitsnetz sonst nie zum Zug.
    requestScan();

    const target = event.target;
    const gallery = target instanceof Element ? target.closest('.card-gallery') : null;
    if (gallery === lastHovered) return;

    if (lastHovered?.isConnected) resetGalleryDeferred(lastHovered);
    lastHovered = gallery;
  },
  { passive: true }
);

// Regulärer Weg: Maus verlässt die Karte.
//
// `pointerleave` feuert für *jedes* Element, das der Zeiger verlässt – auch für
// Kinder tief in der Karte. Da wir in der Capture-Phase am Dokument lauschen,
// bekommen wir sie alle. Nur das Event der Galerie selbst bedeutet wirklich
// "Maus ist von der Karte runter"; alles andere ist Bewegung innerhalb der Karte.
document.addEventListener(
  'pointerleave',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.classList.contains('card-gallery')) return;

    if (target === lastHovered) lastHovered = null;
    resetGalleryDeferred(target);
  },
  { capture: true, passive: true }
);

// Maus verlässt das Browserfenster ganz – dann kommt kein pointerleave mehr.
// Hier mit force, weil der :hover-Zustand in diesem Fall hängen bleiben kann.
document.addEventListener('mouseleave', () => {
  if (lastHovered?.isConnected) resetGalleryForced(lastHovered);
  lastHovered = null;
});

window.addEventListener('blur', () => {
  if (lastHovered?.isConnected) resetGalleryForced(lastHovered);
  lastHovered = null;
});

// Erstes Aufräumen, sobald die Slideshows initialisiert sind. Bewusst über Timeouts
// und nicht über requestAnimationFrame: in verborgenen Tabs – und die Editor-Vorschau
// ist zeitweise genau das – wird rAF nicht ausgeführt, der Reset bliebe dann aus.
setTimeout(resetAll, 0);
setTimeout(resetAll, 400);

// Zurück-Navigation: der Browser stellt die Scroll-Position von Scroll-Containern
// wieder her, Horizon korrigiert das beim Init nicht.
window.addEventListener('pageshow', () => setTimeout(resetAll, 0));

// Tab bzw. Vorschau-iframe wird wieder sichtbar: nachholen, was im Hintergrund
// ausgefallen ist.
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) setTimeout(resetAll, 0);
});

// Theme-Editor: nach jedem Neuladen einer Section aufräumen. Genau hier entstehen
// die verwaisten Pointer-Events, weil der DOM unter dem ruhenden Cursor getauscht wird.
document.addEventListener('shopify:section:load', (event) => {
  lastHovered = null;
  const root = event.target instanceof Element ? event.target : document;
  setTimeout(() => resetAll(root), 0);
});

// Theme-Editor: Beim Auswählen eines Blocks springt Horizon (theme-editor.js) auf
// die nächstgelegene Slideshow – das trifft in Karussells die Bild-Slideshow der Karte.
document.addEventListener('shopify:block:select', () => setTimeout(() => resetAll(), 0));
document.addEventListener('shopify:block:deselect', () => setTimeout(() => resetAll(), 0));
